import { forwardRef, ReactNode, useCallback, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { StyleSheetManager } from 'styled-components';
import { AdminActionButton } from '../../../../Component/UI/AdminPageHeader';
import LiveCompactControls from './LiveCompactControls';

type Props = {
  children: ReactNode;
  onCapture?: (targetDocument: Document) => void;
  onDetachedChange?: (detached: boolean) => void;
  toolbarTarget?: HTMLElement | null;
};

const LivePreviewWindow = forwardRef<HTMLDivElement, Props>(({ children, onCapture, onDetachedChange, toolbarTarget }, ref) => {
  // Keep one portal target: moving it preserves the player, chat and capture reference.
  const [container] = useState(() => document.createElement('div'));
  const [popup, setPopup] = useState<Window | null>(null);
  const [error, setError] = useState('');
  const hostRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<Window | null>(null);
  const wasDetached = useRef(false);
  useImperativeHandle(ref, () => container, [container]);

  useLayoutEffect(() => {
    onDetachedChange?.(!!popup);
    if (wasDetached.current && !popup) openerRef.current?.focus();
    wasDetached.current = !!popup;
  }, [popup, onDetachedChange]);

  const movePreview = useCallback((target: HTMLElement | null) => {
    if (!target || container.parentElement === target) return;
    const video = container.querySelector('video');
    const wasPlaying = video && !video.paused;
    target.appendChild(container);
    // Browsers pause media when its document changes; resume after the move settles.
    if (wasPlaying) target.ownerDocument.defaultView?.requestAnimationFrame(() => {
      if (!video.isConnected || video.ownerDocument !== target.ownerDocument) return;
      void video.play().catch(() => { /* The player's play button handles blocked playback. */ });
    });
  }, [container]);

  useLayoutEffect(() => {
    movePreview(hostRef.current);
    return () => { container.remove(); };
  }, [container, movePreview]);

  const restore = useCallback(() => {
    const detached = popupRef.current;
    movePreview(hostRef.current);
    popupRef.current = null;
    setPopup(null);
    if (detached && !detached.closed) detached.close();
    openerRef.current?.focus();
  }, [movePreview]);

  useLayoutEffect(() => {
    if (!popup) return;
    const syncTheme = () => {
      for (const name of ['data-admin-design', 'data-theme', 'class', 'style', 'lang']) {
        const value = document.documentElement.getAttribute(name);
        if (value === null) popup.document.documentElement.removeAttribute(name);
        else popup.document.documentElement.setAttribute(name, value);
      }
    };
    syncTheme();
    const observer = new MutationObserver(syncTheme);
    observer.observe(document.documentElement, { attributes: true });
    const checkClosed = window.setInterval(() => { if (popup.closed) restore(); }, 500);
    const closePopup = () => popup.close();
    popup.addEventListener('pagehide', restore);
    popup.addEventListener('beforeunload', restore);
    window.addEventListener('pagehide', closePopup);
    return () => {
      observer.disconnect();
      window.clearInterval(checkClosed);
      popup.removeEventListener('pagehide', restore);
      popup.removeEventListener('beforeunload', restore);
      window.removeEventListener('pagehide', closePopup);
      movePreview(hostRef.current);
      popupRef.current = null;
      if (!popup.closed) popup.close();
    };
  }, [popup, movePreview, restore]);

  const open = () => {
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.focus();
      return;
    }
    const detached = window.open('', '_blank', 'popup=yes,width=440,height=820,resizable=yes,scrollbars=yes,toolbar=no,menubar=no,location=no,status=no');
    if (!detached) {
      setError('팝업이 차단되었습니다. 브라우저에서 팝업을 허용한 뒤 다시 눌러주세요.');
      return;
    }
    try {
      detached.document.title = '라이브 화면 | 팬버드';
      const base = detached.document.createElement('base');
      base.href = document.baseURI;
      detached.document.head.appendChild(base);
      const viewport = detached.document.createElement('meta');
      viewport.name = 'viewport';
      viewport.content = 'width=device-width, initial-scale=1';
      detached.document.head.appendChild(viewport);
      // Global CSS/fonts come from the page; styled-components injects live rules below.
      document.querySelectorAll<HTMLLinkElement | HTMLStyleElement>('link[rel="stylesheet"], style:not([data-styled])').forEach(style => {
        detached.document.head.appendChild(style.cloneNode(true));
      });
      detached.document.body.style.margin = '0';
      movePreview(detached.document.body);
      popupRef.current = detached;
      setPopup(detached);
      setError('');
      detached.focus();
    } catch {
      movePreview(hostRef.current);
      detached.close();
      setError('새창을 열지 못했습니다. 다시 눌러주세요.');
    }
  };

  const capture = (target: Document) => {
    onCapture?.(target);
  };

  return <>
    {!popup && <Heading>
      <strong>라이브 화면</strong>
      <Actions>
        <Action ref={openerRef} type="button" onClick={popup ? restore : open}>
          {popup ? '다시 가져오기' : '팝업으로 보기'}
        </Action>
        {!popup && onCapture && <Action type="button" aria-label="상품 이미지 화면 캡처" onClick={() => capture(document)}>화면 캡처</Action>}
      </Actions>
    </Heading>}
    {error && <ErrorMessage role="alert">{error}</ErrorMessage>}
    {popup && (toolbarTarget ? createPortal(<Actions>
      <Action type="button" onClick={restore}>다시 가져오기</Action>
      {onCapture && <Action type="button" aria-label="상품 이미지 화면 캡처" onClick={() => capture(document)}>화면 캡처</Action>}
    </Actions>, toolbarTarget)
      : <Actions><Action type="button" onClick={restore}>다시 가져오기</Action>
        {onCapture && <Action type="button" aria-label="상품 이미지 화면 캡처" onClick={() => capture(document)}>화면 캡처</Action>}
      </Actions>)}
    <div ref={hostRef} />
    {createPortal(<StyleSheetManager target={popup?.document.head}>
      {popup && <LiveCompactControls />}
      <Frame $detached={!!popup}>
        {popup && <PopupToolbar><span>라이브 화면</span><Actions>
          <Action type="button" onClick={restore}>다시 가져오기</Action>
          {onCapture && <Action type="button" aria-label="상품 이미지 화면 캡처" onClick={() => capture(popup.document)}>화면 캡처</Action>}
        </Actions></PopupToolbar>}
        <div>{children}</div>
      </Frame>
    </StyleSheetManager>, container)}
  </>;
});

export default LivePreviewWindow;

const Heading = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: var(--space-2);
  margin-bottom: var(--space-3);
  strong { color: var(--color-text-primary); font-size: var(--text-body-lg); font-weight: 600; }
`;
const Actions = styled.div`display: flex; align-items: center; flex-wrap: wrap; gap: var(--space-2);`;
const Action = styled(AdminActionButton).attrs({ $accented: true })`
  min-height: var(--control-md);
  padding: 0 var(--space-3);
  font-size: var(--text-caption);
  @media (pointer: coarse) { min-height: var(--touch-target); }
`;
const Frame = styled.div<{ $detached: boolean }>`
  overflow: hidden;
  border-radius: ${props => props.$detached ? '0' : 'var(--radius-lg)'};
  ${props => props.$detached && `
    width: 100%;
    height: 100vh;
    height: 100dvh;
    min-height: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    > div:last-child {
      display: flex; flex-direction: column; align-items: stretch; gap: var(--space-3);
      flex: 1; min-width: 0; min-height: 0; overflow: hidden;
    }
    > div:last-child > div { width: 100%; min-width: 0; min-height: 0; max-height: none; flex: 1; }
    > div:last-child > div + div { flex: none; }
    [data-live-media-display] { flex: 1 1 0; aspect-ratio: auto; min-height: 0; }
    [aria-label="라이브 영상"] { padding-bottom: var(--space-4); }
    [aria-label="라이브 영상"] > :not([data-live-media-display]) {
      margin-inline: var(--space-4);
    }
  `}
`;
const PopupToolbar = styled.div`
  box-sizing: border-box;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: var(--space-2);
  min-height: var(--control-xl);
  padding: var(--space-2) var(--space-3);
  background: var(--color-bg-surface);
  color: var(--color-text-primary);
  font: 600 var(--text-caption) var(--font-family-base);
`;
const ErrorMessage = styled.p`color: var(--color-text-danger); font-size: var(--text-caption); line-height: 1.5;`;
